import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faUserGraduate,
  faBookOpen,
  faLayerGroup,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface StudentAdmin {
  userId: string;
  fullName: string;
  enrolledLearnerships: string[];
}

interface LearnershipAdmin {
  id: string;
  title: string;
  isActive: boolean;
  enrolledStudentCount: number;
}

interface EnrollmentAdmin {
  enrollmentId: string;
  studentName: string;
  learnershipTitle: string;
  enrollmentDate: string;
  status: number;
  overallProgressPercent: number;
}

export default function AdminDashboardPage() {
  const [students, setStudents] = useState<StudentAdmin[]>([]);
  const [learnerships, setLearnerships] = useState<LearnershipAdmin[]>([]);
  const [enrollments, setEnrollments] = useState<EnrollmentAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accessToken, user } = useAuth();

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const headers = { Authorization: `Bearer ${accessToken}` };
      const [studentsRes, learnershipsRes, enrollmentsRes] = await Promise.all([
        fetch('/api/admin/students', { headers }),
        fetch('/api/admin/learnerships', { headers }),
        fetch('/api/admin/enrollments', { headers }),
      ]);

      if (!studentsRes.ok || !learnershipsRes.ok || !enrollmentsRes.ok) {
        throw new Error('Failed to load dashboard data');
      }

      setStudents(await studentsRes.json());
      setLearnerships(await learnershipsRes.json());
      setEnrollments(await enrollmentsRes.json());
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const activeLearnerships = learnerships.filter((l) => l.isActive).length;
  const recentEnrollments = enrollments.slice(0, 5);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Welcome back, {user?.firstName}!</h1>
        <p className="text-sm text-brand-900/60 mt-1">Here's an overview of Mokaleng LMS</p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <div className="card-surface p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-brand-900/60">Total Students</p>
                    <p className="text-3xl font-bold text-brand-950 mt-2">{students.length}</p>
                  </div>
                  <FontAwesomeIcon icon={faUserGraduate} className="h-8 w-8 text-brand-600" />
                </div>
              </div>
              <div className="card-surface p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-brand-900/60">Active Courses</p>
                    <p className="text-3xl font-bold text-brand-950 mt-2">{activeLearnerships}</p>
                  </div>
                  <FontAwesomeIcon icon={faBookOpen} className="h-8 w-8 text-brand-600" />
                </div>
              </div>
              <div className="card-surface p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-brand-900/60">Total Enrollments</p>
                    <p className="text-3xl font-bold text-brand-950 mt-2">{enrollments.length}</p>
                  </div>
                  <FontAwesomeIcon icon={faLayerGroup} className="h-8 w-8 text-brand-600" />
                </div>
              </div>
            </div>

            {/* Recent Enrollments */}
            <div className="card-surface p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-brand-950">Recent Enrollments</h2>
                <Link
                  to="/admin/students"
                  className="text-sm font-semibold text-brand-700 hover:text-brand-800 flex items-center gap-1"
                >
                  Manage students <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
                </Link>
              </div>

              {recentEnrollments.length === 0 ? (
                <p className="text-sm text-brand-900/50 text-center py-8">
                  No enrollments yet. Create a course and enroll a student to get started.
                </p>
              ) : (
                <div className="divide-y divide-canvas-200">
                  {recentEnrollments.map((enrollment) => (
                    <div
                      key={enrollment.enrollmentId}
                      className="flex items-center justify-between gap-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-950 truncate">
                          {enrollment.studentName}
                        </p>
                        <p className="text-xs text-brand-900/50 mt-0.5 truncate">
                          {enrollment.learnershipTitle}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-brand-700">
                          {enrollment.overallProgressPercent}%
                        </p>
                        <p className="text-xs text-brand-900/40">
                          {new Date(enrollment.enrollmentDate).toLocaleDateString('en-ZA', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
